import cn from 'classnames';
import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { Button } from '../../../../../../components/button/button';
import { Page } from '../../../../../../components/page/page';
import { Card } from '../../../../../../components/card/card';
import { Container } from '../../../../../../components/container/container';
import { Table } from '../../../../../../features/table/table';
import { TableFilterInput } from '../../../../../../features/table/table-filter/table-filter-input/table-filter-input';
import { TableFilterMultiSelect } from '../../../../../../features/table/table-filter/table-filter-multi-select/table-filter-multi-select';
import { TableFilterSingleSelect } from '../../../../../../features/table/table-filter/table-filter-single-select/table-filter-single-select';
import { TableFilterDateTimeRange } from '../../../../../../features/table/table-filter/table-filter-date-time-range/table-filter-date-time-range';
import { formatRuDateTimeString } from '../../../../../../helpers/date-time';
import { pageKey } from '../../../../../../helpers/path';
import { ExternalMonitoringPeriodDropdown } from '../../../dashboard/dashboard-external-monitoring/external-monitoring-period-dropdown';
import { getSocialIcon } from '../../../../../../helpers/icon-maps';
import { useQueryParams } from '../../../../../../hooks/use-query-params';
import { ExternalReviewItemSentimentType } from '../../../../../../store/api/external-reviews-items-api';
import { useExternalReviews } from './use-external-reviews';
import s from './external-reviews-table.module.css';

const COMMENT_SKIP_MEASURE_MAX_LEN = 40;

type CommentOverflowMeasure = 'pending' | 'overflow' | 'fits';

function ExternalReviewCommentCell({
  comment,
  isExpanded,
  onToggle,
  t,
}: {
  comment: string;
  isExpanded: boolean;
  onToggle: () => void;
  t: (key: string) => string;
}) {
  const measureRef = useRef<HTMLDivElement>(null);
  const [measure, setMeasure] = useState<CommentOverflowMeasure>('pending');

  useLayoutEffect(() => {
    if (comment.trim().length <= COMMENT_SKIP_MEASURE_MAX_LEN) {
      setMeasure('fits');
      return;
    }
    if (isExpanded) return;
    const el = measureRef.current;
    if (!el) return;
    setMeasure(el.scrollHeight > el.clientHeight + 1 ? 'overflow' : 'fits');
  }, [comment, isExpanded]);

  if (comment.trim().length <= COMMENT_SKIP_MEASURE_MAX_LEN) {
    return (
      <div className={s.commentCell}>
        <div className={cn(s.commentBody, s.commentBodyShort)} title={comment}>
          {comment}
        </div>
      </div>
    );
  }

  const showToggle = isExpanded || measure === 'overflow';

  return (
    <div className={s.commentCell}>
      <div
        ref={measureRef}
        className={cn(
          s.commentBody,
          isExpanded && s.commentBodyExpanded,
          !isExpanded && measure === 'fits' && s.commentBodyShort,
          !isExpanded && (measure === 'overflow' || measure === 'pending') && s.commentBodyCollapsed,
        )}
        title={comment}
      >
        {comment}
      </div>
      {showToggle ? (
        <Button
          type="button"
          variant="secondary"
          size="sm"
          outline
          className={s.commentToggleBtn}
          onClick={onToggle}
        >
          {isExpanded ? t('Свернуть') : t('Показать полностью')}
        </Button>
      ) : null}
    </div>
  );
}

const sentimentOptions = [
  { id: 'positive', value: 'Позитивный' },
  { id: 'negative', value: 'Негативный' },
  { id: 'mixed', value: 'Смешанный' },
];

const getSentimentEmoji = (sentiment: ExternalReviewItemSentimentType): string => {
  switch (sentiment) {
    case 'positive':
      return '😊';
    case 'negative':
      return '😞';
    case 'mixed':
      return '😐';
    default:
      return '❓';
  }
};

export const ExternalReviewsTable = () => {
  const {
    externalReviewItems,
    isLoadingFetch,
    t,
    periodHintText,
    usesCreatedAtColumnRange,
    tablePeriodBeginSec,
    tablePeriodEndSec,
    languageFilterOptions,
  } = useExternalReviews();
  const { addQueryParams } = useQueryParams();
  const [expandedCommentKeys, setExpandedCommentKeys] = useState<Set<string>>(new Set());

  const toggleCommentExpanded = useCallback((rowKey: string) => {
    setExpandedCommentKeys((prev) => {
      const next = new Set(prev);
      if (next.has(rowKey)) next.delete(rowKey);
      else next.add(rowKey);
      return next;
    });
  }, []);

  return (
    <Page className={s.page}>
      <Card className={s.filtersCard}>
        <div className={s.filtersRow}>
          <Container label={t('Период')}>
            {usesCreatedAtColumnRange ? (
              <span className={s.periodOverrideNote}>{t('Задаётся фильтром колонки «Дата»')}</span>
            ) : (
              <ExternalMonitoringPeriodDropdown
                periodBeginSec={tablePeriodBeginSec}
                periodEndSec={tablePeriodEndSec}
                onRangeChange={(begin, end) =>
                  addQueryParams({
                    comments_begin: String(begin),
                    comments_end: String(end),
                    comments_period: undefined,
                    [pageKey]: '1',
                  })
                }
              />
            )}
          </Container>
        </div>
        {periodHintText ? <div className={s.periodHint}>{periodHintText}</div> : null}
      </Card>
      <Card>
        <Table
          id="external-reviews"
          columnSettings
          frontPagination
          isLoading={isLoadingFetch}
          onEmpty={{
            title: t('Список внешних отзывов пуст'),
            text: t('Внешние отзывы появятся после настройки источников'),
          }}
          cols={{
            source: {
              label: t('Источник'),
              filter: (
                <TableFilterInput
                  name="source"
                  suggestOptions={externalReviewItems
                    ?.map((item) => item.source?.name)
                    .filter((name): name is string => Boolean(name))}
                />
              ),
            },
            comment: {
              label: t('Комментарий'),
              filter: (
                <TableFilterInput
                  name="comment"
                  suggestOptions={externalReviewItems?.map((item) => item.comment)}
                />
              ),
            },
            language: {
              label: t('Язык'),
              filter: (
                <TableFilterSingleSelect options={languageFilterOptions} name="review_language" />
              ),
            },
            sentiment: {
              label: t('Тональность'),
              filter: (
                <TableFilterMultiSelect
                  options={sentimentOptions}
                  name="sentiment"
                />
              ),
            },
            createdAt: {
              label: t('Дата'),
              filter: <TableFilterDateTimeRange name="created_at" />,
            },
          }}
          rows={externalReviewItems?.map((item) => ({
            rowId: item.rowKey,
            source: item.source ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ display: 'flex', alignItems: 'center' }}>
                  {getSocialIcon(item.source.icon)}
                </span>
                <span>{item.source.name}</span>
              </div>
            ) : null,
            comment: (
              <ExternalReviewCommentCell
                comment={item.comment}
                isExpanded={expandedCommentKeys.has(item.rowKey)}
                onToggle={() => toggleCommentExpanded(item.rowKey)}
                t={t}
              />
            ),
            language:
              item.language_id === null ? (
                <span className={s.inProgressMuted}>{t('В процессе')}</span>
              ) : (
                item.language_label || '—'
              ),
            sentiment: (() => {
              if (item.sentiment_in_progress) {
                return <span className={s.inProgressMuted}>{t('В процессе')}</span>;
              }
              const sent = item.sentiment ?? 'mixed';
              const label =
                sent === 'positive'
                  ? t('Позитивный')
                  : sent === 'negative'
                    ? t('Негативный')
                    : t('Смешанный');
              const pct = item.sentiment_percentages;
              const sentimentParts: { value: number; emoji: string; color: string }[] = [
                { value: pct?.positive ?? 0, emoji: '😊', color: '#10b981' },
                { value: pct?.neutral ?? 0, emoji: '😐', color: '#f59e0b' },
                { value: pct?.negative ?? 0, emoji: '😞', color: '#ef4444' },
              ]
                .filter((x) => x.value > 0)
                .sort((a, b) => b.value - a.value);
              const hasAnyPct = sentimentParts.length > 0;
              return (
                <div className={s.sentiment} title={label}>
                  {hasAnyPct ? (
                    <div className={s.sentimentPercentages}>
                      {sentimentParts.map((part) => (
                        <span
                          key={part.emoji}
                          className={s.percentage}
                          style={{ color: part.color }}
                        >
                          <span className={s.sentimentEmojiInline} aria-hidden>
                            {part.emoji}
                          </span>{' '}
                          {part.value}%
                        </span>
                      ))}
                    </div>
                  ) : (
                    <span className={s.sentimentEmojiOnly} aria-label={label}>
                      {getSentimentEmoji(sent)}
                    </span>
                  )}
                </div>
              );
            })(),
            createdAt: formatRuDateTimeString(item.created_at),
          }))}
        />
      </Card>
    </Page>
  );
};
